import { useState } from 'react';
import { T, useGT } from 'gt-react';
import type { APInvoiceSnoozePreset } from '@erp/shared-types';
import { useSnoozeAPInvoices } from '@/api/rpc/hooks/accounts-payable/useSnoozeAPInvoices';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/context/ToastContext';
import {
  SnoozeDialogDescription,
  SnoozePresetPicker,
  SnoozeSubmitLabel,
  SnoozeWarningCopy,
} from './InvoiceSnoozeDialogParts';
import {
  fromDateTimeLocalValue,
  isSnoozeInstantInPast,
  readSnoozeConfirmation,
  snoozePresetUntil,
  snoozeUntilLocalDate,
  toDateTimeLocalValue,
  type SnoozeWarnings,
} from './invoiceSnooze';

/**
 * Snooze one draft invoice or a selection. Presets are 9:00 in the browser's
 * timezone. The server rejects a time in the past and asks for confirmation
 * when an open task exists or the snooze outlasts the due date.
 */
export function SnoozeInvoicesDialog({
  invoiceIds,
  open,
  onOpenChange,
  onCompleted,
}: {
  invoiceIds: string[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after a snooze is saved. Closing the dialog does not call this. */
  onCompleted?: () => void;
}) {
  const gt = useGT();
  const { showToast } = useToast();
  const [form, setForm] = useState(() => ({
    preset: 'tomorrow' as APInvoiceSnoozePreset,
    specific: toDateTimeLocalValue(snoozePresetUntil('tomorrow')),
    note: '',
    clearsOnEdit: true,
  }));
  const [warnings, setWarnings] = useState<SnoozeWarnings | null>(null);
  const { preset, specific, note, clearsOnEdit } = form;
  const snooze = useSnoozeAPInvoices();

  const until =
    preset === 'specific'
      ? fromDateTimeLocalValue(specific)
      : snoozePresetUntil(preset);
  const untilIsPast = isSnoozeInstantInPast(until, new Date());

  function resetWarnings() {
    setWarnings(null);
  }

  async function submit(confirms: SnoozeWarnings) {
    if (!until || untilIsPast) {
      showToast({
        title: gt('Choose a future time', {
          context: 'toast when an invoice snooze time is in the past',
        }),
        color: 'error',
      });
      return;
    }
    try {
      const result = await snooze.mutateAsync({
        invoiceIds,
        until,
        untilLocalDate: snoozeUntilLocalDate(until),
        preset,
        note: note.trim() || undefined,
        clearsOnEdit,
        confirmTasks: confirms.tasks,
        confirmPastDue: confirms.pastDue,
      });
      showSnoozeResult(
        gt,
        showToast,
        result.invoices.length,
        result.skipped.length,
      );
      resetWarnings();
      onCompleted?.();
      onOpenChange(false);
    } catch (error) {
      const confirmation = readSnoozeConfirmation(error);
      if (confirmation) {
        setWarnings(confirmation);
        return;
      }
      showToast({
        title: gt('Could not snooze', {
          context: 'toast when snoozing an invoice fails',
        }),
        message: error instanceof Error ? error.message : undefined,
        color: 'error',
      });
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) resetWarnings();
        onOpenChange(next);
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            <T context="title of the snooze invoice dialog">Snooze</T>
          </DialogTitle>
          <DialogDescription>
            <SnoozeDialogDescription count={invoiceIds.length} />
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <SnoozePresetPicker
            preset={preset}
            specific={specific}
            onPreset={(next) => {
              setForm((current) => ({ ...current, preset: next }));
              resetWarnings();
            }}
            onSpecific={(value) => {
              setForm((current) => ({ ...current, specific: value }));
              resetWarnings();
            }}
          />
          <div className="space-y-1">
            <Label htmlFor="invoice-snooze-note">
              <T context="optional note on an invoice snooze">Note</T>
            </Label>
            <Textarea
              id="invoice-snooze-note"
              value={note}
              onChange={(event) =>
                setForm((current) => ({ ...current, note: event.target.value }))
              }
              placeholder={gt('Optional', {
                context: 'placeholder for the optional invoice snooze note',
              })}
            />
          </div>
          <label className="flex items-start gap-2 text-sm">
            <Checkbox
              checked={clearsOnEdit}
              onCheckedChange={(checked) =>
                setForm((current) => ({
                  ...current,
                  clearsOnEdit: checked === true,
                }))
              }
            />
            <span>
              <T context="checkbox that clears an invoice snooze when someone edits the invoice">
                Clear this snooze when the invoice is edited
              </T>
            </span>
          </label>
          {warnings && (
            <SnoozeWarningCopy warnings={warnings} count={invoiceIds.length} />
          )}
        </div>
        <DialogFooter>
          <Button
            type="button"
            disabled={snooze.isPending || invoiceIds.length === 0}
            onClick={() => {
              void submit(warnings ?? { tasks: false, pastDue: false });
            }}
          >
            <SnoozeSubmitLabel confirming={warnings != null} />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function showSnoozeResult(
  gt: ReturnType<typeof useGT>,
  showToast: (toast: { title: string; color: 'success' }) => void,
  snoozed: number,
  skipped: number,
) {
  showToast({
    title:
      skipped > 0
        ? gt('Snoozed {count}. {skipped} could not be snoozed.', {
            context: 'toast after snoozing invoices when some were skipped',
            count: String(snoozed),
            skipped: String(skipped),
          })
        : gt('Invoice snoozed', {
            context: 'toast after snoozing an invoice',
          }),
    color: 'success',
  });
}
